/**
 * The "More" filter pill folds four filters into one dropdown: feature tag,
 * activity tag, created-date range, and hide-completed. Keeping the pill count
 * down matters beyond tidiness — the filters row scrolls horizontally, and a
 * scrolling row is what triggered the WebKit paint bug that made every dropdown
 * invisible in Safari (see helpers/filterMenuLayer.ts).
 *
 * The date range counts as ONE filter even when both ends are set, and
 * hide-completed counts as active only when it is off, because hiding completed
 * hikes is the default.
 */

import { i18n } from '../../../i18n';

export interface MoreFilterState {
  featureTag: string | null;
  activityTag: string | null;
  startDate: string | null;
  endDate: string | null;
  /** `true` (the default) hides completed hikes. */
  hideCompleted: boolean;
}

export const MORE_FILTER_DEFAULTS: MoreFilterState = {
  featureTag: null,
  activityTag: null,
  startDate: null,
  endDate: null,
  hideCompleted: true,
};

/** How many of the four grouped filters are set to a non-default value. */
export function countActiveMoreFilters(state: MoreFilterState): number {
  let n = 0;
  if (state.featureTag) n += 1;
  if (state.activityTag) n += 1;
  if (state.startDate || state.endDate) n += 1; // one range, not two filters
  if (state.hideCompleted === false) n += 1; // default is true
  return n;
}

export function isMoreFilterActive(state: MoreFilterState): boolean {
  return countActiveMoreFilters(state) > 0;
}

/** Pill label: bare "More" until something is set, then "More (n)". */
export function moreFilterLabel(state: MoreFilterState): string {
  const n = countActiveMoreFilters(state);
  const t = i18n.global.t;
  return n > 0 ? t('hikes.more_filters.more_count', { count: n }) : t('shared.more');
}
